import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import { ConfirmModal } from '../../components/Common/ConfirmModal';
import { useToast } from '../../components/Common/Toast';

interface SocialPost {
    id: number;
    date_post: string;
    platform: string;
    status: string;
    template_type?: string | null;
    caption?: string | null;
    media_id?: string | null;
    permalink?: string | null;
    error_message?: string | null;
    created_at?: string | null;
}

interface CalendarSlot {
    status: string;
    template_type: string | null;
    permalink: string | null;
}

interface CalendarDay {
    date: string;
    is_trading_day: boolean;
    closing: CalendarSlot | null;
    editorial: CalendarSlot | null;
}

type PreviewSlot = 'daily' | 'weekly' | 'education_mindset' | 'education_rules' | 'education_quiz';

// Brouillon stocke dans le Blob par POST /v1/admin/social-posts/preview : c'est
// exactement cette image et cette legende que "Publier" enverra sur Instagram.
interface SocialPreview {
    slot: PreviewSlot;
    date_post: string;
    post_kind: string;
    template_type: string | null;
    caption: string;
    generated_at: string;
    published: boolean;
    published_at?: string | null;
    image_url: string;
}

interface PreviewResponse {
    data: SocialPreview | null;
    detail?: string;
}

interface PublishResponse {
    status?: string;
    message?: string;
    detail?: string;
    permalink?: string | null;
    from_preview?: boolean;
}

interface BusyState {
    key: string;
    action: 'preview' | 'publish';
}

const PUBLISH_ENDPOINTS: Record<PreviewSlot, string> = {
    daily: 'publish-daily-social-post',
    weekly: 'publish-weekly-social-post',
    education_mindset: 'publish-education-social-post',
    education_rules: 'publish-education-rules-social-post',
    education_quiz: 'publish-education-quiz-social-post',
};

const EDUCATION_SLOTS: { slot: PreviewSlot; label: string; description: string }[] = [
    { slot: 'education_mindset', label: 'Mindset', description: 'Deux volets contrastés sur un principe d\'investissement (texte Gemini).' },
    { slot: 'education_rules', label: 'Règles d\'or', description: '3 règles d\'or BRVM tirées du catalogue (thème aléatoire).' },
    { slot: 'education_quiz', label: 'Quiz / dilemme', description: 'Un dilemme d\'investisseur en deux options (tirage aléatoire).' },
];

const previewKey = (slot: PreviewSlot, dateStr?: string): string => `${slot}|${dateStr || 'today'}`;

const STATUS_STYLES: Record<string, { bg: string; fg: string; border: string; label: string }> = {
    SUCCESS: { bg: '#d1fae5', fg: '#065f46', border: '#6ee7b7', label: 'Publié' },
    FAILED: { bg: '#fee2e2', fg: '#991b1b', border: '#fca5a5', label: 'Échec' },
};

const TEMPLATE_LABELS: Record<string, string> = {
    gainers_summary: 'Hausse',
    losers_summary: 'Baisse',
    dividend_alert: 'Dividende',
    ranking_top_ytd: 'Classement YTD',
    ranking_top_dividend_yield: 'Classement dividende',
    ranking_sector_ytd: 'Classement sectoriel',
    education_mindset: 'Éducation',
    education_rules: 'Règles d\'or',
    education_quiz: 'Quiz',
    company_snapshot: 'Fiche société',
    sector_duel: 'Duel sectoriel',
    earnings_recap: 'Résultats',
};

const MONTH_NAMES = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];
const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const style = STATUS_STYLES[status] || { bg: '#f1f5f9', fg: '#64748b', border: '#cbd5e1', label: status };
    return (
        <span style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: '12px',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            backgroundColor: style.bg,
            color: style.fg,
            border: `1px solid ${style.border}`,
        }}>
            {style.label}
        </span>
    );
};

const inputStyle: React.CSSProperties = {
    padding: '6px 8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '13px',
    backgroundColor: 'var(--bg-input, white)',
};

const truncate = (text: string | null | undefined, max: number): string => {
    if (!text) return '—';
    return text.length > max ? text.slice(0, max - 1).trimEnd() + '…' : text;
};

const formatDateTime = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatDayLabel = (dateStr: string): string => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};

// Couleurs de la cellule calendrier — rouge = trou a combler (jour de bourse
// sans post de cloture), vert = publie, gris = marche ferme (rien a faire).
const dayCellStyle = (day: CalendarDay, isSelected: boolean): React.CSSProperties => {
    let bg = '#f1f5f9', border = '#e2e8f0', color = '#94a3b8';
    if (!day.is_trading_day) {
        bg = '#f8fafc'; border = '#eef2f7'; color = '#cbd5e1';
    } else if (day.closing?.status === 'SUCCESS') {
        bg = '#d1fae5'; border = '#6ee7b7'; color = '#065f46';
    } else if (day.closing?.status === 'FAILED') {
        bg = '#fee2e2'; border = '#fca5a5'; color = '#991b1b';
    } else {
        bg = '#fef3c7'; border = '#fcd34d'; color = '#92400e'; // trading day, aucun post -> le trou
    }
    return {
        border: `2px solid ${isSelected ? '#4f46e5' : border}`,
        backgroundColor: bg,
        color,
        borderRadius: '8px',
        padding: '8px 6px',
        minHeight: '58px',
        cursor: day.is_trading_day ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        fontSize: '13px',
        fontWeight: 700,
        position: 'relative',
    };
};

const primaryButtonStyle: React.CSSProperties = {
    backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px',
    padding: '9px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
};
const secondaryButtonStyle: React.CSSProperties = {
    backgroundColor: 'transparent', color: '#4f46e5', border: '1px solid #4f46e5', borderRadius: '6px',
    padding: '9px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
};

interface SlotActionsProps {
    label: string;
    description?: string;
    preview: SocialPreview | null | undefined;
    busyAction: BusyState['action'] | null;
    disabled: boolean;
    onPreview: () => void;
    onPublish: () => void;
}

// Un format de post : Previsualiser (genere + stocke le brouillon) puis Publier
// (publie CE brouillon s'il existe et n'a pas deja ete publie).
const SlotActions: React.FC<SlotActionsProps> = ({ label, description, preview, busyAction, disabled, onPreview, onPublish }) => {
    const pendingDraft = !!preview && !preview.published;
    return (
        <div style={{ padding: '12px 0', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600 }}>{label}</div>
                    {description && <div style={{ fontSize: '12px', color: '#64748b' }}>{description}</div>}
                </div>
                <button onClick={onPreview} disabled={disabled} style={secondaryButtonStyle}>
                    {busyAction === 'preview' ? 'Génération…' : preview ? 'Régénérer l\'aperçu' : 'Prévisualiser'}
                </button>
                <button onClick={onPublish} disabled={disabled} style={primaryButtonStyle}>
                    {busyAction === 'publish' ? 'Publication…' : pendingDraft ? 'Publier cet aperçu' : 'Publier'}
                </button>
            </div>

            {preview && (
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '12px' }}>
                    <a href={preview.image_url} target='_blank' rel='noreferrer' title='Ouvrir en taille réelle (1080×1350)'>
                        <img
                            src={preview.image_url}
                            alt={`Aperçu — ${label}`}
                            style={{ width: '270px', maxWidth: '100%', aspectRatio: '1080 / 1350', objectFit: 'cover', display: 'block', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                        />
                    </a>
                    <div style={{ flex: '1 1 240px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            Aperçu généré le {formatDateTime(preview.generated_at)}
                            {preview.published && <StatusBadge status='SUCCESS' />}
                        </div>
                        <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '13px', margin: 0, maxHeight: '240px', overflowY: 'auto' }}>
                            {preview.caption}
                        </pre>
                        {preview.published && (
                            <p style={{ fontSize: '12px', color: '#065f46', margin: 0 }}>
                                Cet aperçu a été publié le {formatDateTime(preview.published_at)}.
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const AdminSocialPostsScreen: React.FC = () => {
    const { token, hasPermission } = useAuth();
    const [posts, setPosts] = useState<SocialPost[]>([]);
    const [loading, setLoading] = useState(false);
    const [platformFilter, setPlatformFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [deletingId, setDeletingId] = useState<number | null>(null);

    const today = new Date();
    const [calendarYear, setCalendarYear] = useState(today.getFullYear());
    const [calendarMonth, setCalendarMonth] = useState(today.getMonth() + 1); // 1-12
    const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [previews, setPreviews] = useState<Record<string, SocialPreview | null>>({});
    const [busy, setBusy] = useState<BusyState | null>(null);
    // Confirmation en modale, jamais window.confirm (règle d'or du 2026-10-01) :
    // le message nomme l'action et son effet.
    const [confirmation, setConfirmation] = useState<{
        titre: string; message: string; libelle: string; destructive: boolean; executer: () => void;
    } | null>(null);
    const { showToast } = useToast();

    const fetchPosts = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (platformFilter) params.set('platform', platformFilter);
            if (statusFilter) params.set('status', statusFilter);
            if (dateFrom) params.set('date_from', dateFrom);
            if (dateTo) params.set('date_to', dateTo);
            const qs = params.toString() ? `?${params.toString()}` : '';
            const response = await fetch(`${API_URL}/v1/admin/social-posts${qs}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setPosts(Array.isArray(data) ? data : []);
            } else {
                setPosts([]);
            }
        } catch (error) {
            console.error('Error fetching social posts:', error);
            setPosts([]);
        } finally {
            setLoading(false);
        }
    }, [token, platformFilter, statusFilter, dateFrom, dateTo]);

    const fetchCalendar = useCallback(async () => {
        if (!token) return;
        setCalendarLoading(true);
        try {
            const response = await fetch(
                `${API_URL}/v1/admin/social-posts/calendar?year=${calendarYear}&month=${calendarMonth}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.ok) {
                const data = await response.json();
                setCalendarDays(Array.isArray(data) ? data : []);
            } else {
                setCalendarDays([]);
            }
        } catch (error) {
            console.error('Error fetching social posts calendar:', error);
            setCalendarDays([]);
        } finally {
            setCalendarLoading(false);
        }
    }, [token, calendarYear, calendarMonth]);

    // « Supprimer » n'efface que la ligne d'historique, pas le post Instagram : une
    // republication sur la même date crée un doublon si le post n'est pas archivé
    // à la main sur Instagram (audit 2026-10-17, T12).
    const handleDelete = (postId: number, datePost: string) => {
        if (!token) return;
        setConfirmation({
            titre: 'Supprimer la ligne d\'historique',
            message: `Supprime la ligne d'historique du post du ${datePost} pour permettre d'en régénérer un sur cette date. `
                + 'Le post déjà publié reste en ligne sur Instagram : archivez-le ou supprimez-le vous-même dans l\'application '
                + 'Instagram, sinon la nouvelle publication fera doublon.',
            libelle: 'Supprimer la ligne',
            destructive: true,
            executer: () => { void supprimerLigne(postId); },
        });
    };

    const supprimerLigne = async (postId: number) => {
        setDeletingId(postId);
        try {
            const response = await fetch(`${API_URL}/v1/admin/social-posts/${postId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok || response.status === 204) {
                setPosts(prev => prev.filter(p => p.id !== postId));
                fetchCalendar();
                showToast('Ligne d\'historique supprimée. Pensez à archiver le post sur Instagram.', 'success');
            } else {
                showToast('La ligne d\'historique n\'a pas pu être supprimée.', 'error');
            }
        } catch (error) {
            console.error('Error deleting social post:', error);
            showToast('La ligne d\'historique n\'a pas pu être supprimée.', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    // Lecture seule : brouillon deja genere pour ce format/date (null s'il n'y en a pas).
    const loadPreview = useCallback(async (slot: PreviewSlot, dateStr?: string) => {
        if (!token) return;
        const params = new URLSearchParams({ slot });
        if (dateStr) params.set('target_date', dateStr);
        try {
            const response = await fetch(`${API_URL}/v1/admin/social-posts/preview?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const body: PreviewResponse = await response.json();
                setPreviews(prev => ({ ...prev, [previewKey(slot, dateStr)]: body.data }));
            }
        } catch (error) {
            console.error('Error fetching social post preview:', error);
        }
    }, [token]);

    // Genere le post (donnees + texte + image) et le stocke comme brouillon. Ne publie rien.
    const handlePreview = async (slot: PreviewSlot, dateStr?: string) => {
        if (!token) return;
        const key = previewKey(slot, dateStr);
        setBusy({ key, action: 'preview' });
        const params = new URLSearchParams({ slot });
        if (dateStr) params.set('target_date', dateStr);
        try {
            const response = await fetch(`${API_URL}/v1/admin/social-posts/preview?${params.toString()}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            const body: PreviewResponse = await response.json().catch(() => ({ data: null }));
            if (response.ok) {
                setPreviews(prev => ({ ...prev, [key]: body.data }));
            } else {
                showToast('La prévisualisation a échoué. Consultez les journaux de l\'API pour le détail.', 'error');
            }
        } catch (error) {
            console.error('Preview error:', error);
            showToast('La prévisualisation a échoué (serveur injoignable).', 'error');
        } finally {
            setBusy(null);
        }
    };

    // use_preview=true : publie l'aperçu affiche s'il existe et n'a pas deja ete publie,
    // sinon genere et publie directement (comportement historique).
    const handlePublish = (slot: PreviewSlot, dateStr?: string) => {
        if (!token) return;
        const draft = previews[previewKey(slot, dateStr)];
        setConfirmation({
            titre: 'Publier sur Instagram',
            message: draft && !draft.published
                ? 'Publie réellement l\'aperçu affiché sur Instagram (et Facebook si configuré). Le post sera visible immédiatement.'
                : 'Aucun aperçu en attente : le post sera généré puis publié réellement sur Instagram, sans relecture. Le post sera visible immédiatement.',
            libelle: 'Publier',
            destructive: true,
            executer: () => { void publier(slot, dateStr); },
        });
    };

    const publier = async (slot: PreviewSlot, dateStr?: string) => {
        const key = previewKey(slot, dateStr);
        setBusy({ key, action: 'publish' });
        const params = new URLSearchParams({ use_preview: 'true' });
        if (dateStr) params.set('target_date', dateStr);
        try {
            const response = await fetch(`${API_URL}/v1/internal/${PUBLISH_ENDPOINTS[slot]}?${params.toString()}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            const data: PublishResponse = await response.json().catch(() => ({}));
            if (response.ok && (data.status === 'SUCCESS' || data.status === 'PARTIAL')) {
                await Promise.all([fetchCalendar(), fetchPosts(), loadPreview(slot, dateStr)]);
                showToast(data.status === 'PARTIAL'
                    ? 'Publication partielle : une des plateformes a échoué (voir l\'historique ci-dessous).'
                    : 'Publication effectuée.', data.status === 'PARTIAL' ? 'warning' : 'success');
            } else if (data.status === 'SKIPPED') {
                showToast(data.message || 'Déjà publié pour cette date.', 'info');
            } else {
                showToast('La publication a échoué : le détail est dans l\'historique ci-dessous.', 'error');
                fetchPosts();
            }
        } catch (error) {
            console.error('Publish error:', error);
            showToast('La publication a échoué (serveur injoignable).', 'error');
        } finally {
            setBusy(null);
        }
    };

    const busyActionFor = (slot: PreviewSlot, dateStr?: string): BusyState['action'] | null =>
        busy && busy.key === previewKey(slot, dateStr) ? busy.action : null;

    useEffect(() => {
        if (token && hasPermission('admin_platform')) {
            fetchPosts();
        }
    }, [token, hasPermission, fetchPosts]);

    useEffect(() => {
        if (token && hasPermission('admin_platform')) {
            fetchCalendar();
        }
    }, [token, hasPermission, fetchCalendar]);

    // Aperçus déjà générés pour le jour sélectionné dans le calendrier.
    useEffect(() => {
        if (token && selectedDate && hasPermission('admin_platform')) {
            loadPreview('daily', selectedDate);
            loadPreview('weekly', selectedDate);
        }
    }, [token, selectedDate, hasPermission, loadPreview]);

    // Aperçus éditoriaux du jour.
    useEffect(() => {
        if (token && hasPermission('admin_platform')) {
            EDUCATION_SLOTS.forEach(({ slot }) => loadPreview(slot));
        }
    }, [token, hasPermission, loadPreview]);

    if (!hasPermission('admin_platform')) {
        return (
            <SiteLayout>
                <Box><p style={{ padding: '20px' }}>Accès refusé.</p></Box>
            </SiteLayout>
        );
    }

    const goToPreviousMonth = () => {
        setSelectedDate(null);
        if (calendarMonth === 1) { setCalendarMonth(12); setCalendarYear(y => y - 1); }
        else { setCalendarMonth(m => m - 1); }
    };
    const goToNextMonth = () => {
        setSelectedDate(null);
        if (calendarMonth === 12) { setCalendarMonth(1); setCalendarYear(y => y + 1); }
        else { setCalendarMonth(m => m + 1); }
    };

    // Grille 7 colonnes (lun-dim) : decalage initial selon le jour de semaine du 1er du mois.
    const firstOfMonth = new Date(calendarYear, calendarMonth - 1, 1);
    const leadingBlanks = (firstOfMonth.getDay() + 6) % 7; // getDay(): 0=dim -> 0=lun
    const selectedDay = calendarDays.find(d => d.date === selectedDate) || null;

    return (
        <SiteLayout>
            <Header icon='photo_camera' title='Publications Réseaux Sociaux' />

            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding'>
                    <p>Calendrier de publication — recap de clôture</p>
                </div>
                <div className='box-content box-horizontal-padding' style={{ padding: '20px' }}>
                    <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                        Un jour de bourse en <strong style={{ color: '#92400e' }}>ambre</strong> n'a pas de recap de clôture publié —
                        cliquez dessus pour le prévisualiser puis le publier (quotidien ou bilan hebdomadaire).
                        Les jours <strong style={{ color: '#64748b' }}>gris</strong> sont hors marché (weekend/férié), rien à publier.
                    </p>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
                        <button onClick={goToPreviousMonth} style={{ backgroundColor: 'transparent', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '14px' }}>
                            ← Précédent
                        </button>
                        <div style={{ fontSize: '16px', fontWeight: 700, minWidth: '160px', textAlign: 'center' }}>
                            {MONTH_NAMES[calendarMonth - 1]} {calendarYear}
                        </div>
                        <button onClick={goToNextMonth} style={{ backgroundColor: 'transparent', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '14px' }}>
                            Suivant →
                        </button>
                        {calendarLoading && <span style={{ fontSize: '13px', color: '#94a3b8' }}>Chargement…</span>}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', maxWidth: '640px' }}>
                        {WEEKDAY_LABELS.map(w => (
                            <div key={w} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>
                                {w}
                            </div>
                        ))}
                        {Array.from({ length: leadingBlanks }).map((_, i) => <div key={`blank-${i}`} />)}
                        {calendarDays.map(day => {
                            const dayNum = parseInt(day.date.split('-')[2], 10);
                            const isSelected = selectedDate === day.date;
                            return (
                                <div
                                    key={day.date}
                                    style={dayCellStyle(day, isSelected)}
                                    onClick={() => day.is_trading_day && setSelectedDate(isSelected ? null : day.date)}
                                    title={day.is_trading_day ? (day.closing ? `Clôture : ${day.closing.status}` : 'Aucun post de clôture') : 'Marché fermé'}
                                >
                                    <span>{dayNum}</span>
                                    {day.editorial && (
                                        <span style={{
                                            width: '6px', height: '6px', borderRadius: '50%',
                                            backgroundColor: day.editorial.status === 'SUCCESS' ? '#4f46e5' : '#f87171',
                                            position: 'absolute', bottom: '6px',
                                        }} title={`Éditorial : ${day.editorial.status}`} />
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {selectedDay && (
                        <div style={{
                            marginTop: '20px', padding: '16px 20px', borderRadius: '8px',
                            backgroundColor: 'var(--bg-hover, #f8fafc)', border: '1px solid #e2e8f0',
                            maxWidth: '640px',
                        }}>
                            <p style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, textTransform: 'capitalize' }}>
                                {formatDayLabel(selectedDay.date)}
                            </p>
                            {selectedDay.closing?.status === 'SUCCESS' ? (
                                <p style={{ fontSize: '13px', color: '#065f46' }}>
                                    Déjà publié ({TEMPLATE_LABELS[selectedDay.closing.template_type || ''] || selectedDay.closing.template_type}).{' '}
                                    {selectedDay.closing.permalink && <a href={selectedDay.closing.permalink} target='_blank' rel='noreferrer'>Voir le post</a>}
                                </p>
                            ) : (
                                <div>
                                    <SlotActions
                                        label='Résumé quotidien'
                                        preview={previews[previewKey('daily', selectedDay.date)]}
                                        busyAction={busyActionFor('daily', selectedDay.date)}
                                        disabled={busy !== null}
                                        onPreview={() => handlePreview('daily', selectedDay.date)}
                                        onPublish={() => handlePublish('daily', selectedDay.date)}
                                    />
                                    <SlotActions
                                        label='Bilan hebdomadaire'
                                        preview={previews[previewKey('weekly', selectedDay.date)]}
                                        busyAction={busyActionFor('weekly', selectedDay.date)}
                                        disabled={busy !== null}
                                        onPreview={() => handlePreview('weekly', selectedDay.date)}
                                        onPublish={() => handlePublish('weekly', selectedDay.date)}
                                    />
                                </div>
                            )}
                            {selectedDay.closing?.status === 'FAILED' && (
                                <p style={{ fontSize: '12px', color: '#991b1b', marginTop: '8px' }}>
                                    Dernière tentative en échec — republier réessaiera depuis zéro.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </Box>

            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding'>
                    <p>Contenu éditorial — Éducation (publication du jour)</p>
                </div>
                <div className='box-content box-horizontal-padding' style={{ padding: '20px' }}>
                    <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px', maxWidth: '760px' }}>
                        <strong>Prévisualiser</strong> génère l'image et la légende et les conserve comme aperçu, sans rien publier.
                        {' '}<strong>Publier</strong> envoie ensuite exactement cet aperçu sur Instagram / Facebook.
                        Un seul post éditorial par jour : s'il a déjà été publié aujourd'hui, la publication est ignorée.
                    </p>
                    <div style={{ maxWidth: '760px' }}>
                        {EDUCATION_SLOTS.map(({ slot, label, description }) => (
                            <SlotActions
                                key={slot}
                                label={label}
                                description={description}
                                preview={previews[previewKey(slot)]}
                                busyAction={busyActionFor(slot)}
                                disabled={busy !== null}
                                onPreview={() => handlePreview(slot)}
                                onPublish={() => handlePublish(slot)}
                            />
                        ))}
                    </div>
                </div>
            </Box>

            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding'>
                    <p>Historique des posts Instagram/Facebook générés automatiquement</p>
                </div>
                <div className='box-content box-horizontal-padding' style={{ padding: '20px' }}>
                    <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                        Contenu texte uniquement (légende, statut, horodatage) — les images ne sont pas
                        conservées au-delà de leur durée de vie temporaire sur le stockage Blob.
                    </p>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                        <select value={platformFilter} onChange={e => setPlatformFilter(e.target.value)} style={inputStyle}>
                            <option value="">Toutes les plateformes</option>
                            <option value="instagram">Instagram</option>
                            <option value="facebook">Facebook</option>
                        </select>

                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={inputStyle}>
                            <option value="">Tous les statuts</option>
                            <option value="SUCCESS">Publié</option>
                            <option value="FAILED">Échec</option>
                        </select>

                        <label style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Du
                            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
                        </label>
                        <label style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Au
                            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
                        </label>

                        {(platformFilter || statusFilter || dateFrom || dateTo) && (
                            <button
                                onClick={() => { setPlatformFilter(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); }}
                                style={{ backgroundColor: 'transparent', color: '#64748b', border: '1px solid #cbd5e1', padding: '9px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
                            >
                                Réinitialiser les filtres
                            </button>
                        )}

                        <button
                            onClick={() => fetchPosts()}
                            disabled={loading}
                            style={{ backgroundColor: 'transparent', color: '#4f46e5', border: '1px solid #4f46e5', padding: '9px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                        >
                            Actualiser
                        </button>
                    </div>

                    {posts.length >= 100 && (
                        <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '12px' }}>
                            Affichage limité aux 100 publications les plus récentes pour ce filtre.
                        </p>
                    )}

                    {loading ? (
                        <p>Chargement...</p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className='data-table' style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Date</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Publié le</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Plateforme</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Type</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'center' }}>Statut</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Légende</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Détail</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'center' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {posts.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                                Aucune publication pour ce filtre.
                                            </td>
                                        </tr>
                                    ) : posts.map((p, idx) => (
                                        <tr key={p.id} style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}>
                                            <td style={{ padding: '12px 16px', fontSize: '13px', whiteSpace: 'nowrap' }}>{p.date_post}</td>
                                            <td style={{ padding: '12px 16px', fontSize: '13px', whiteSpace: 'nowrap' }}>{formatDateTime(p.created_at)}</td>
                                            <td style={{ padding: '12px 16px', fontSize: '13px', textTransform: 'capitalize' }}>{p.platform}</td>
                                            <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.template_type ? (TEMPLATE_LABELS[p.template_type] || p.template_type) : '—'}</td>
                                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                <StatusBadge status={p.status} />
                                            </td>
                                            <td style={{ padding: '12px 16px', fontSize: '13px', maxWidth: '360px' }} title={p.caption || ''}>
                                                {truncate(p.caption, 90)}
                                            </td>
                                            <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                                                {p.status === 'SUCCESS' && p.permalink ? (
                                                    <a href={p.permalink} target='_blank' rel='noreferrer'>Voir le post</a>
                                                ) : p.status === 'FAILED' && p.error_message ? (
                                                    <span style={{ color: '#991b1b' }} title={p.error_message}>{truncate(p.error_message, 60)}</span>
                                                ) : '—'}
                                            </td>
                                            <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                <button
                                                    onClick={() => handleDelete(p.id, p.date_post)}
                                                    disabled={deletingId === p.id}
                                                    title="Supprimer la ligne pour permettre la régénération du post"
                                                    style={{
                                                        backgroundColor: 'transparent',
                                                        color: '#ef4444',
                                                        border: '1px solid #fca5a5',
                                                        padding: '4px 10px',
                                                        borderRadius: '6px',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {deletingId === p.id ? '...' : 'Supprimer'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Box>
            <ConfirmModal
                isOpen={confirmation !== null}
                title={confirmation?.titre ?? ''}
                message={confirmation?.message ?? ''}
                confirmLabel={confirmation?.libelle}
                isDestructive={confirmation?.destructive ?? false}
                onConfirm={() => {
                    const action = confirmation;
                    setConfirmation(null);
                    action?.executer();
                }}
                onCancel={() => setConfirmation(null)}
            />
        </SiteLayout>
    );
};

export default AdminSocialPostsScreen;
